/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  noteWorksheetInputs,
  submissionTypeFields,
  submissionTypeOptions,
} from "./noteWorksheetInputs";
import WorksheetAcknowledgment from "../components/worksheet/WorksheetAcknowledgment";
import { InputField } from "../types/worksheet";
import LoadingSpinner from "../components/loading/LoadingSpinner";
import { formatPaperBrokerSubmissionData } from "../helpers/formatPaperBrokerSubmissionData";
import { paperBrokerSubmissionApi } from "../api";
import {
  faCheckCircle,
  faXmarkCircle,
  faTrash,
  faArrowRight,
  faArrowLeft,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate, useLocation } from "react-router-dom";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

interface OwnerInfo {
  // Owner fields
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  mailingAddress: string;
  city: string;
  state: string;
  zipCode: string;

  // Your info fields (for birddog and broker)
  yourFirstName: string;
  yourLastName: string;
  yourEmail: string;
  yourPhone: string;
  yourMailingAddress: string;
  yourCity: string;
  yourState: string;
  yourZipCode: string;

  // Owner info when birddog is selected
  ownerFirstName: string;
  ownerLastName: string;
  ownerEmail: string;
  ownerPhone: string;
  ownerMailingAddress: string;
  ownerCity: string;
  ownerState: string;
  ownerZipCode: string;
}

interface PropertyInfo {
  propertyAddress: string;
  city: string;
  state: string;
  zipCode: string;
  propertyType: string;
  yearBuilt: string;
  acreage: string;
  propertyDescription: string;
  occupancy: string;
  estimatedValue: string;
}

interface NoteInfo {
  noteDate: string;
  propertySalePrice: string;
  downPaymentAmount: string;
  originalStartingBalance: string;
  currentPrincipleBalance: string;
  interestRate: string;
  paymentAmount: string;
  paymentOptions: string;
  nextPaymentDate: string;
  maturityDate: string;
  balloonPaymentDate: string;
  hasBalloonPayment: string;
  paymentsCurrent: string;
  totalDebt: string;
  paymentCollector: string;
  thirdPartyServicerName: string;
  isFirstPosition: string;
  noteAskingPrice: string;
  specialCircumstances: string;
  documents: File[];
  additionalComments: string;
}

interface FormData {
  submissionType: "owner" | "birddog" | "broker";
  isTestMode: boolean;
  ownerInfo: OwnerInfo;
  propertyInfo: PropertyInfo;
  noteInfo: NoteInfo;
}

interface FormErrors {
  [key: string]: string;
}

const NoteWorksheet = () => {
  const query = useQuery();
  const mode = query.get("mode");

  const isTestMode = mode === "test";

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(() => {
    // Default form data structure
    const defaultFormData = {
      submissionType: "owner",
      isTestMode: isTestMode,
      ownerInfo: {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        mailingAddress: "",
        city: "",
        state: "",
        zipCode: "",
        yourFirstName: "",
        yourLastName: "",
        yourEmail: "",
        yourPhone: "",
        yourMailingAddress: "",
        yourCity: "",
        yourState: "",
        yourZipCode: "",
        ownerFirstName: "",
        ownerLastName: "",
        ownerEmail: "",
        ownerPhone: "",
        ownerMailingAddress: "",
        ownerCity: "",
        ownerState: "",
        ownerZipCode: "",
      },
      propertyInfo: {
        propertyAddress: "",
        city: "",
        state: "",
        zipCode: "",
        propertyType: "",
        yearBuilt: "",
        acreage: "",
        propertyDescription: "",
        occupancy: "",
        estimatedValue: "",
      },
      noteInfo: {
        noteDate: "",
        propertySalePrice: "",
        downPaymentAmount: "",
        originalStartingBalance: "",
        currentPrincipleBalance: "",
        interestRate: "",
        paymentAmount: "",
        paymentOptions: "",
        nextPaymentDate: "",
        maturityDate: "",
        balloonPaymentDate: "",
        hasBalloonPayment: "",
        paymentsCurrent: "",
        totalDebt: "",
        paymentCollector: "",
        thirdPartyServicerName: "",
        isFirstPosition: "",
        noteAskingPrice: "",
        specialCircumstances: "",
        documents: [],
        additionalComments: "",
      },
    };

    // Try to load saved form data from localStorage
    const savedData = localStorage.getItem("noteWorksheetData");
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        // Merge the saved data with the default structure
        return {
          ...defaultFormData,
          submissionType:
            parsedData.submissionType || defaultFormData.submissionType,
          ownerInfo: {
            ...defaultFormData.ownerInfo,
            ...parsedData.ownerInfo,
          },
        };
      } catch (error) {
        console.error("Error parsing saved form data:", error);
        return defaultFormData;
      }
    }
    // Return default form data if nothing is saved or there was an error
    return defaultFormData;
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [hasAcknowledged, setHasAcknowledged] = useState(() => {
    // Try to load acknowledgment state from localStorage
    const savedAcknowledgment = localStorage.getItem(
      "noteWorksheetAcknowledged"
    );
    return savedAcknowledgment === "true";
  });

  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const [hasStoredData, setHasStoredData] = useState(false);

  // Function to check if there's data in localStorage
  const checkLocalStorage = useCallback(() => {
    const hasData = localStorage.getItem("noteWorksheetData") !== null;
    setHasStoredData(hasData);
  }, []);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    // Check if the form has any non-empty values
    const hasValues =
      Object.values(formData.ownerInfo).some((value) => value !== "") ||
      Object.values(formData.propertyInfo).some((value) => value !== "") ||
      Object.values(formData.noteInfo).some((value) => {
        if (typeof value === "object") return false; // Skip document objects
        return value !== "";
      });

    if (hasValues) {
      // Only save to localStorage if we have actual data
      const formDataForStorage = {
        ...formData,
        noteInfo: {
          ...formData.noteInfo,
          documents: [], // Don't store File objects
        },
      };
      localStorage.setItem(
        "noteWorksheetData",
        JSON.stringify(formDataForStorage)
      );
    }

    // Update hasStoredData after saving to localStorage
    checkLocalStorage();
  }, [formData, checkLocalStorage]);

  // Also try to restore the current step
  const [currentStepFromStorage, setCurrentStepFromStorage] = useState(() => {
    const savedStep = localStorage.getItem("noteWorksheetStep");
    return savedStep ? parseInt(savedStep, 10) : 1;
  });

  // Update currentStep from storage on initial load
  useEffect(() => {
    setCurrentStep(currentStepFromStorage);
  }, [currentStepFromStorage]);

  // Save current step to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("noteWorksheetStep", currentStep.toString());
  }, [currentStep]);

  useEffect(() => {
    localStorage.setItem(
      "noteWorksheetAcknowledged",
      hasAcknowledged.toString()
    );
  }, [hasAcknowledged]);

  const buttonRef = useRef<HTMLButtonElement>(null);

  const attentionAcknowledgement = () => {
    if (buttonRef.current) {
      buttonRef.current.classList.add("animate-attention");
      setTimeout(() => {
        buttonRef.current?.classList.remove("animate-attention");
      }, 820);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    if (!hasAcknowledged) {
      e.preventDefault();
      attentionAcknowledgement();
      return;
    }
    const { name, value, type } = e.target;

    // Handle radio button for submission type
    if (name === "submissionType") {
      setFormData((prev) => ({
        ...prev,
        submissionType: value as "owner" | "birddog" | "broker",
      }));
      return;
    }

    const [section, field] = name.split(".");

    if (type === "file") {
      const fileInput = e.target as HTMLInputElement;
      const files = fileInput.files ? Array.from(fileInput.files) : [];
      setFormData((prev) => ({
        ...prev,
        [section]: {
          ...(prev[section as keyof FormData] as Record<string, any>),
          [field]: files,
        },
      }));
      return;
    }

    // Find the field definition to check for coercion rules
    let coercedValue = value;
    const fieldConfig = [
      ...submissionTypeFields,
      ...noteWorksheetInputs.ownerInfo,
      ...noteWorksheetInputs.propertyInfo,
      ...noteWorksheetInputs.noteInfo,
    ].find((f) => f.section === section && f.name === field);

    if (fieldConfig?.coercion && fieldConfig.coercion.length > 0) {
      for (const coercion of fieldConfig.coercion) {
        const coercionType = coercion.type;
        const coercionValue = coercion.value;

        switch (coercionType) {
          case "exactLength":
            // Limit input to exactly N characters
            if (coercedValue.length > coercionValue) {
              coercedValue = coercedValue.slice(0, coercionValue);
            }
            break;
          case "minDecimals":
            // Ensure at least N decimal places
            if (coercedValue) {
              const parts = coercedValue.toString().split(".");
              if (parts.length === 1) {
                // No decimal point, add one with required zeros
                coercedValue = `${parts[0]}.${Array(coercionValue)
                  .fill("0")
                  .join("")}`;
              } else if (parts[1].length < coercionValue) {
                // Add zeros to reach required decimal places
                coercedValue = `${parts[0]}.${parts[1].padEnd(
                  coercionValue,
                  "0"
                )}`;
              }
            }
            break;
          case "maxDigits":
            // Limit to maximum N digits
            if (coercedValue) {
              const numericValue = coercedValue.toString().replace(/\D/g, "");
              if (numericValue.length > coercionValue) {
                coercedValue = numericValue.slice(0, coercionValue);
              }
            }
            break;
          case "numeric":
            // Ensure the value is a number
            if (coercedValue) {
              const numericValue = parseFloat(coercedValue);
              if (isNaN(numericValue)) {
                coercedValue = "";
              }
            }
            break;
          case "maxDecimals":
            if (coercedValue) {
              const numericValue = parseFloat(coercedValue);
              if (isNaN(numericValue)) {
                coercedValue = "";
              } else {
                const parts = coercedValue.toString().split(".");
                if (parts.length > 1 && parts[1].length > coercionValue) {
                  // Only truncate if there are more decimal places than allowed
                  coercedValue = `${parts[0]}.${parts[1].substring(
                    0,
                    coercionValue
                  )}`;
                }
              }
            }
            break;
          default:
            break;
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section as keyof FormData] as Record<string, any>),
        [field]: coercedValue,
      },
    }));
  };

  const validateField = (field: InputField, value: any): string | null => {
    if (!field.validation) return null;

    for (const rule of Array.isArray(field.validation)
      ? field.validation
      : field.validation(formData)) {
      switch (rule.type) {
        case "required":
          if (!value || value === "") {
            return rule.message;
          }
          break;
        case "email":
          if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            return rule.message;
          }
          break;
        case "phone":
          if (value && !/^\d{10}$/.test(value.replace(/\D/g, ""))) {
            return rule.message;
          }
          break;
        case "minValue":
          if (
            value &&
            rule.value !== undefined &&
            parseFloat(value) < (rule.value as number)
          ) {
            return rule.message;
          }
          break;
        case "maxValue":
          if (
            value &&
            rule.value !== undefined &&
            parseFloat(value) > (rule.value as number)
          ) {
            return rule.message;
          }
          break;
        case "range":
          if (value && rule.value) {
            const numValue = parseFloat(value);
            const rangeValue = rule.value as { min: number; max: number };
            if (numValue < rangeValue.min || numValue > rangeValue.max) {
              return rule.message;
            }
          }
          break;
        case "minLength":
          if (
            value &&
            rule.value !== undefined &&
            value.length < (rule.value as number)
          ) {
            return rule.message;
          }
          break;
        case "maxLength":
          if (
            value &&
            rule.value !== undefined &&
            value.length > (rule.value as number)
          ) {
            return rule.message;
          }
          break;
        case "pattern":
          if (
            value &&
            rule.value !== undefined &&
            !new RegExp(rule.value as string).test(value)
          ) {
            return rule.message;
          }
          break;
      }
    }

    return null;
  };

  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    switch (step) {
      case 1:
        // Validate fields based on submission type
        noteWorksheetInputs.ownerInfo.forEach((field) => {
          // Only validate fields that should be shown based on the submission type
          if (field.required && field.showWhen && field.showWhen(formData)) {
            const value = formData.ownerInfo[field.name as keyof OwnerInfo];
            const errorMessage = validateField(field, value);
            if (errorMessage) {
              newErrors[`ownerInfo.${field.name}`] = errorMessage;
              isValid = false;
            }
          }
        });
        break;
      case 2:
        // Validate property info fields using the input configuration
        noteWorksheetInputs.propertyInfo.forEach((field) => {
          if (field.required) {
            const value =
              formData.propertyInfo[field.name as keyof PropertyInfo];
            const errorMessage = validateField(field, value);
            if (errorMessage) {
              newErrors[`propertyInfo.${field.name}`] = errorMessage;
              isValid = false;
            }
          }
        });
        break;
      case 3:
        // Validate note info fields using the input configuration
        noteWorksheetInputs.noteInfo.forEach((field) => {
          if (field.required) {
            const value = formData.noteInfo[field.name as keyof NoteInfo];
            const errorMessage = validateField(field, value);
            if (errorMessage) {
              newErrors[`noteInfo.${field.name}`] = errorMessage;
              isValid = false;
            }
          }
        });
        break;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (currentStep === 3 && validateStep(currentStep)) {
      setIsSubmitting(true);
      setSubmissionStatus(null);
      try {
        const submissionData = formatPaperBrokerSubmissionData(formData);

        const formDataObj = new FormData();

        formDataObj.append("data", JSON.stringify(submissionData));

        if (formData.noteInfo.documents.length > 0) {
          for (const file of formData.noteInfo.documents) {
            formDataObj.append("documents", file);
          }
        }

        await paperBrokerSubmissionApi.createSubmission(formDataObj);

        localStorage.removeItem("noteWorksheetData");

        const savedData = {
          submissionType: formData.submissionType,
          ownerInfo: {},
        };

        if (formData.submissionType === "owner") {
          savedData.ownerInfo = {
            firstName: formData.ownerInfo.firstName,
            lastName: formData.ownerInfo.lastName,
            email: formData.ownerInfo.email,
            phone: formData.ownerInfo.phone,
            mailingAddress: formData.ownerInfo.mailingAddress,
            city: formData.ownerInfo.city,
            state: formData.ownerInfo.state,
            zipCode: formData.ownerInfo.zipCode,
          };
        } else {
          savedData.ownerInfo = {
            yourFirstName: formData.ownerInfo.yourFirstName,
            yourLastName: formData.ownerInfo.yourLastName,
            yourEmail: formData.ownerInfo.yourEmail,
            yourPhone: formData.ownerInfo.yourPhone,
            yourMailingAddress: formData.ownerInfo.yourMailingAddress,
            yourCity: formData.ownerInfo.yourCity,
            yourState: formData.ownerInfo.yourState,
            yourZipCode: formData.ownerInfo.yourZipCode,
          };
        }

        // Save the filtered data back to localStorage
        localStorage.setItem("noteWorksheetData", JSON.stringify(savedData));
        localStorage.setItem("noteWorksheetStep", "1");

        window.dispatchEvent(new Event("storage"));

        checkLocalStorage();
      } catch (error) {
        console.error("Form submission error:", error);

        // Set error status
        setSubmissionStatus({
          success: false,
          message:
            "There was an error submitting your note. Please try again or contact support.",
        });
      } finally {
        setIsSubmitting(false);
        navigate("/online-note-worksheet-success");
      }
    }
  };

  // Render form field based on its type
  const renderField = (field: InputField, index: number) => {
    const fieldName = `${field.section}.${field.name}`;

    // Get the field value with proper typing
    const fieldValue =
      field.section === "ownerInfo"
        ? (formData.ownerInfo as any)[field.name]
        : field.section === "propertyInfo"
        ? (formData.propertyInfo as any)[field.name]
        : (formData.noteInfo as any)[field.name];

    const fieldError = errors[fieldName];

    // Check if field should be shown based on conditions
    if (field.showWhen && !field.showWhen(formData)) {
      return null;
    }

    const baseClasses =
      "mt-1 block w-full h-8 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 bg-gray-100 text-black font-medium placeholder:text-gray-500 px-2";

    // Handle different field types
    if (
      field.type === "text" ||
      field.type === "email" ||
      field.type === "tel" ||
      field.type === "date" ||
      field.type === "month"
    ) {
      return (
        <div className={field.fullWidth ? "md:col-span-2" : ""} key={fieldName}>
          <label className="block text-sm font-medium text-gray-200">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <input
            type={field.type}
            name={fieldName}
            value={fieldValue as string}
            onChange={handleInputChange}
            className={baseClasses}
            placeholder={field.placeholder}
          />
          {fieldError && (
            <p className="mt-1 text-sm text-red-500">{fieldError}</p>
          )}
          {field.section === "ownerInfo" && index === 14 && (
            <div className="w-full h-9" />
          )}
        </div>
      );
    }

    if (field.type === "number") {
      return (
        <div className={field.fullWidth ? "md:col-span-2" : ""} key={fieldName}>
          <label className="block text-sm font-medium text-gray-200">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <input
            type="number"
            name={fieldName}
            value={fieldValue as string}
            onChange={handleInputChange}
            min={field.min}
            max={field.max}
            step={field.step}
            className={baseClasses}
            placeholder={field.placeholder}
          />
          {fieldError && (
            <p className="mt-1 text-sm text-red-500">{fieldError}</p>
          )}
        </div>
      );
    }

    if (field.type === "select") {
      return (
        <div className={field.fullWidth ? "md:col-span-2" : ""} key={fieldName}>
          <label className="block text-sm font-medium text-gray-200">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <select
            name={fieldName}
            value={fieldValue as string}
            onChange={handleInputChange}
            className={baseClasses}
          >
            <option value="">Select {field.label.toLowerCase()}</option>
            {field.options &&
              field.options.map((option) => {
                const value =
                  typeof option === "string" ? option : option.value;
                const label =
                  typeof option === "string" ? option : option.label;
                return (
                  <option key={value} value={value}>
                    {label}
                  </option>
                );
              })}
          </select>
          {fieldError && (
            <p className="mt-1 text-sm text-red-500">{fieldError}</p>
          )}
        </div>
      );
    }

    if (field.type === "radio") {
      return (
        <div className={field.fullWidth ? "md:col-span-2" : ""} key={fieldName}>
          <label className="block text-sm font-bold text-gray-200">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div className="mt-2 space-y-2">
            {field.options &&
              field.options.map((option) => {
                const value =
                  typeof option === "string" ? option : option.value;
                const label =
                  typeof option === "string" ? option : option.label;
                return (
                  <label className="flex items-center" key={value}>
                    <input
                      type="radio"
                      name={fieldName}
                      value={value}
                      checked={fieldValue === value}
                      onChange={handleInputChange}
                      className="form-radio radio "
                    />
                    <span className="ml-2">{label}</span>
                  </label>
                );
              })}
          </div>
          {fieldError && (
            <p className="mt-1 text-sm text-red-500">{fieldError}</p>
          )}
        </div>
      );
    }

    if (field.type === "textarea") {
      return (
        <div className={field.fullWidth ? "md:col-span-2" : ""} key={fieldName}>
          <label className="block text-sm font-medium text-gray-200">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <textarea
            name={fieldName}
            value={fieldValue as string}
            onChange={handleInputChange}
            rows={4}
            className={baseClasses}
            placeholder={field.placeholder}
          />
          {fieldError && (
            <p className="mt-1 text-sm text-red-500">{fieldError}</p>
          )}
        </div>
      );
    }

    if (field.type === "file") {
      return (
        <div className={field.fullWidth ? "md:col-span-2" : ""} key={fieldName}>
          <label className="block text-sm font-medium text-gray-200">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <input
            type="file"
            name={fieldName}
            onChange={handleInputChange}
            className="mt-1 block w-full"
            multiple
          />
          {fieldValue && fieldValue.length > 0 && (
            <div className="mt-2">
              <p className="text-sm text-gray-200">
                {fieldValue.length} file(s) selected
              </p>
            </div>
          )}
          {fieldError && (
            <p className="mt-1 text-sm text-red-500">{fieldError}</p>
          )}
        </div>
      );
    }

    return null;
  };

  // Check localStorage on initial render
  useEffect(() => {
    checkLocalStorage();

    // Also set up a storage event listener to update when localStorage changes
    const handleStorageChange = () => {
      checkLocalStorage();
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [checkLocalStorage]);

  // Function to clear form data and localStorage
  const handleClearForm = () => {
    // Clear localStorage first
    localStorage.removeItem("noteWorksheetData");
    localStorage.removeItem("noteWorksheetStep");
    localStorage.removeItem("noteWorksheetAcknowledged");

    // Force update hasStoredData state immediately
    setHasStoredData(false);

    // Manually dispatch a storage event to ensure other listeners update
    window.dispatchEvent(new Event("storage"));

    // Reset form to initial state
    setFormData({
      submissionType: "owner",
      isTestMode: isTestMode,
      ownerInfo: {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        mailingAddress: "",
        city: "",
        state: "",
        zipCode: "",
        yourFirstName: "",
        yourLastName: "",
        yourEmail: "",
        yourPhone: "",
        yourMailingAddress: "",
        yourCity: "",
        yourState: "",
        yourZipCode: "",
        ownerFirstName: "",
        ownerLastName: "",
        ownerEmail: "",
        ownerPhone: "",
        ownerMailingAddress: "",
        ownerCity: "",
        ownerState: "",
        ownerZipCode: "",
      },
      propertyInfo: {
        propertyAddress: "",
        city: "",
        state: "",
        zipCode: "",
        propertyType: "",
        yearBuilt: "",
        acreage: "",
        propertyDescription: "",
        occupancy: "",
        estimatedValue: "",
      },
      noteInfo: {
        noteDate: "",
        propertySalePrice: "",
        downPaymentAmount: "",
        originalStartingBalance: "",
        currentPrincipleBalance: "",
        interestRate: "",
        paymentAmount: "",
        paymentOptions: "",
        nextPaymentDate: "",
        maturityDate: "",
        hasBalloonPayment: "",
        balloonPaymentDate: "",
        paymentsCurrent: "",
        totalDebt: "",
        paymentCollector: "",
        thirdPartyServicerName: "",
        isFirstPosition: "",
        noteAskingPrice: "",
        specialCircumstances: "",
        documents: [],
        additionalComments: "",
      },
    });

    // Reset to step 1
    setCurrentStep(1);

    // Reset acknowledgment
    setHasAcknowledged(false);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="relative">
        <h1 className="text-3xl font-bold text-center mb-8 text-[#181E35]">
          Note Worksheet
        </h1>
      </div>

      {/* Animated Submission Status Message */}
      <AnimatePresence>
        {submissionStatus && (
          <motion.div
            key="submissionStatus"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`p-4 mb-6 rounded-md ${
              submissionStatus.success
                ? "bg-green-100 text-green-800 border-2 border-green-400 rounded-xl"
                : "bg-red-100 text-red-800 border-2 border-red-400 rounded-xl"
            }`}
          >
            <FontAwesomeIcon
              icon={submissionStatus.success ? faCheckCircle : faXmarkCircle}
              className="w-4 h-4 mr-2"
            />
            {submissionStatus.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Disclaimer Section */}
      <WorksheetAcknowledgment
        hasAcknowledged={hasAcknowledged}
        onAcknowledge={() => setHasAcknowledged(true)}
        buttonRef={buttonRef}
      />

      {/* Animated Form Content */}
      <form className="space-y-6 bg-[#181E35] rounded-xl p-4">
        <AnimatePresence mode="wait">
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              <h2 className="text-2xl font-semibold mb-4 text-gray-50">
                Who Are You?
              </h2>
              <div className="space-y-4">
                <div className="flex space-x-4">
                  {submissionTypeOptions.map((option) => (
                    <label
                      className="flex items-center font-semibold"
                      key={option.value}
                    >
                      <input
                        type="radio"
                        name="submissionType"
                        value={option.value}
                        checked={formData.submissionType === option.value}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500 radio radio-md"
                      />
                      <span className="ml-2 text-sm text-gray-200">
                        {option.label}
                      </span>
                    </label>
                  ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {noteWorksheetInputs.ownerInfo.map((field, index) =>
                    renderField(field, index)
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {currentStep === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              <h2 className="text-2xl font-semibold mb-4 text-gray-50">
                Collateral Property Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {noteWorksheetInputs.propertyInfo.map((field, index) =>
                  renderField(field, index)
                )}
              </div>
            </motion.div>
          )}

          {currentStep === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              <h2 className="text-2xl font-semibold mb-4 text-gray-50">
                Promissory Note Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {noteWorksheetInputs.noteInfo.map((field, index) =>
                  renderField(field, index)
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={handlePrevious}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mr-2"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              BACK
            </button>
          )}
          {hasStoredData && (
            <button
              onClick={handleClearForm}
              className=" bg-red-700 hover:bg-red-600 text-white px-4 py-2 rounded-md text-sm transition-colors duration-200 btn font-medium"
              title="Clear form data"
            >
              <FontAwesomeIcon icon={faTrash} className="mr-2" />
              CLEAR
            </button>
          )}
          {currentStep < 3 ? (
            <button
              type="button"
              onClick={handleNext}
              className="ml-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-bold text-white bg-[#61CE70] hover:bg-[#61CE70] focus:outline-none w-24 select-none"
            >
              NEXT
              <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
            </button>
          ) : (
            <button
              type="button"
              onClick={isSubmitting ? () => {} : handleSubmit}
              className="ml-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-bold text-white bg-[#61CE70] hover:bg-[#61CE70] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 w-24 flex items-center justify-center"
            >
              {isSubmitting ? <LoadingSpinner /> : "SUBMIT"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default NoteWorksheet;
